import * as React from "react"

import { AppFunctions } from "@others/AppFunctions.ts"
import { AppIndexedDBKeys } from "@constants/AppIndexedDBKeys.ts"
import { IGetUserLoggedResponseCli } from "@interfaces/IGetUserLoggedResponseCli.ts"

import IndexedDB from "@services/indexedDBService.ts"

interface ICtx {
	isLogged: boolean
	setIsLogged: React.Dispatch<React.SetStateAction<boolean>>
	loggedUser?: IGetUserLoggedResponseCli
	setLoggedUser: React.Dispatch<
		React.SetStateAction<IGetUserLoggedResponseCli | undefined>
	>
}

const InitialCtxState: ICtx = {
	isLogged: AppFunctions.isAuthenticated(),
	setIsLogged: () => {},
	setLoggedUser: () => {},
}

let isLoggedUserInitialState = true

export const Context = React.createContext<ICtx>(InitialCtxState)

const CtxProvider: React.FC<{
	children: React.ReactNode
}> = ({ children }) => {
	const [isLogged, setIsLogged] = React.useState<ICtx["isLogged"]>(
		InitialCtxState.isLogged
	)

	const [loggedUser, setLoggedUserState] =
		React.useState<ICtx["loggedUser"]>()

	React.useEffect(() => {
		if (isLoggedUserInitialState) return
		IndexedDB.updateKey(AppIndexedDBKeys.LOGGED_USER, loggedUser)
	}, [loggedUser])

	React.useEffect(() => {
		// getting the initial statue from indexedDB
		IndexedDB.getKey<ICtx["loggedUser"]>(AppIndexedDBKeys.LOGGED_USER).then(
			(data) => {
				if (!data) return
				setLoggedUserState(data)
			}
		)
	}, [])

	const setLoggedUser: ICtx["setLoggedUser"] = React.useCallback(
		(loggedUser) => {
			isLoggedUserInitialState = false
			setLoggedUserState(loggedUser)
		},
		[]
	)

	const contextValue = React.useMemo(
		() => ({
			isLogged,
			setIsLogged,
			loggedUser,
			setLoggedUser,
		}),
		[isLogged, setIsLogged, loggedUser, setLoggedUser]
	)

	return <Context.Provider value={contextValue}>{children}</Context.Provider>
}

export const AuthCtxProvider = CtxProvider
